import { makeAutoObservable } from "mobx";
import type { ToplistApi } from "@/api/toplist";
import { Resource } from "./Resource";
import { isGeneralCategory } from "@/core/generalCategory";

type ComparisonStatus = "idle" | "loading" | "loaded" | "unavailable";

/** The key of the latest chart, which is asked for without a day */
const LATEST = "";

export class ToplistStore {
    /** The chart for a day, or the latest one */
    readonly page: Resource<ToplistResponse, string>;
    /** The chart before it, which the movement of each position is measured against */
    readonly previousPage: Resource<ToplistEntry[], string>;
    categoryFilter: string[] = [];
    authorFilter: string[] = [];
    freeFilter = "";
    onlyNew = false;

    constructor(private readonly api: ToplistApi) {
        this.page = new Resource((day) => this.api.chart(day === LATEST ? undefined : day));
        this.previousPage = new Resource((day) => this.api.chart(day).then((chart) => chart.toplist));

        makeAutoObservable<ToplistStore, "api">(this, { api: false, page: false, previousPage: false });
    }

    get toplist(): ToplistEntry[] {
        return this.page.data?.toplist ?? [];
    }

    /** The previous day's chart, once it has loaded */
    get yesterday(): ToplistEntry[] {
        return this.previousPage.data ?? [];
    }

    get current(): ToplistMeta | null {
        const page = this.page.data;
        return page
            ? {
                  date: page.date,
                  count: page.count,
                  cached: page.cached,
                  latest: page.latest,
                  previous: page.previous,
                  next: page.next,
              }
            : null;
    }

    /** Whether the previous chart is there to compare with, on its way, or not to be had */
    get comparisonStatus(): ComparisonStatus {
        const previous = this.page.data?.previous;
        if (!this.page.data) {
            return "idle";
        }
        if (!previous) {
            return "unavailable";
        }
        if (!this.previousPage.isFor(previous)) {
            return "idle";
        }
        if (this.previousPage.loading) {
            return "loading";
        }
        return this.previousPage.loaded ? "loaded" : "unavailable";
    }

    /** Loads the chart for a day, or the latest, unless it is loaded. Rejects when that fails. */
    async loadToplist(day: string | undefined = undefined) {
        await this.page.load(day ?? LATEST);
        if (this.page.status === "error") {
            throw this.page.error;
        }
    }

    /**
     * Loads a chart together with the previous day used for comparison. Settles when both are there, so a
     * page that suspends on it never shows positions without their movement. Rejects when the chart itself
     * cannot be loaded; a missing comparison only leaves every movement unknown.
     */
    async openToplist(day: string | undefined = undefined) {
        await this.loadToplist(day);
        const previous = this.page.data?.previous;
        if (!previous) {
            this.previousPage.reset();
            return;
        }
        await this.previousPage.load(previous);
        if (this.previousPage.status === "error") {
            console.error("Failed to load previous toplist:", this.previousPage.error);
        }
    }

    /** True when the loaded chart (and its comparison) is the one for `day`, or the latest when no day is given. */
    isOpen(day: string | undefined) {
        const comparisonDone = this.comparisonStatus === "loaded" || this.comparisonStatus === "unavailable";
        const matches = day ? this.current?.date === day : this.current?.latest === true;
        return comparisonDone && matches;
    }

    matchesCategoryFilter(entry: ToplistEntry) {
        if (this.categoryFilter.length === 0) {
            return true;
        }
        return this.categoryFilter.some((filter: string) => {
            return entry.podcast ? (entry.podcast.categories ?? []).some((cat: Category) => cat.name === filter) : true;
        });
    }

    matchesAuthorFilter(entry: ToplistEntry) {
        if (this.authorFilter.length === 0) {
            return true;
        }
        return this.authorFilter.some((filter: string) => {
            return entry.podcast ? entry.podcast.author.name === filter : true;
        });
    }

    getYesterdaysEntry(podcastId: number): ToplistEntry | undefined {
        return this.yesterday.find((entry: ToplistEntry) => entry.podcast_id === podcastId);
    }

    getMovement(entry: ToplistEntry): { diff: number | null; isNew: boolean; isLoading: boolean } {
        if (this.comparisonStatus !== "loaded") {
            return { diff: null, isNew: false, isLoading: this.comparisonStatus === "loading" };
        }

        const previousEntry = this.getYesterdaysEntry(entry.podcast_id);
        if (!previousEntry) {
            return { diff: null, isNew: true, isLoading: false };
        }

        return { diff: previousEntry.pos - entry.pos, isNew: false, isLoading: false };
    }

    get entries() {
        return this.toplist
            .filter((entry: ToplistEntry) => this.matchesCategoryFilter(entry))
            .filter((entry: ToplistEntry) => this.matchesAuthorFilter(entry))
            .filter((entry: ToplistEntry) => this.matchesFreeFilter(entry));
    }

    get newEntries() {
        return this.entries.filter((entry: ToplistEntry) => {
            if (!entry.podcast || !entry.podcast.entries_count) {
                return false;
            }
            return entry.podcast.entries_count === 1;
        });
    }

    isNewEntry(entryId: number): boolean {
        return this.newEntries.some((entry: ToplistEntry) => entry.id === entryId);
    }

    get groupedCategories() {
        const groups: GroupedCategories[] = [];

        this.entries.forEach((entry: ToplistEntry) => {
            // oxlint-disable-next-line no-unused-expressions
            entry.podcast &&
                (entry.podcast.categories ?? []).forEach((cat: Category) => {
                    if (!groups.some((group: GroupedCategories) => group.category.name === cat.name)) {
                        groups.push({ category: cat, count: 1 });
                    } else {
                        const existing = groups.find((group: GroupedCategories) => group.category.name === cat.name);
                        if (existing) {
                            existing.count++;
                        }
                    }
                });
        });

        return groups
            .sort((a: GroupedCategories, b: GroupedCategories) => b.count - a.count)
            .filter((group: GroupedCategories) => !isGeneralCategory(group.category));
    }

    get groupedAuthors() {
        const groups: GroupedAuthor[] = [];

        this.entries.forEach((entry: ToplistEntry) => {
            if (entry.podcast && entry.podcast.author) {
                if (!groups.some((group: GroupedAuthor) => group.author.id === entry.podcast?.author.id)) {
                    groups.push({ author: entry.podcast?.author, count: 1 });
                } else {
                    const existing = groups.find(
                        (group: GroupedAuthor) => group.author.id === entry.podcast?.author.id
                    );
                    if (existing) {
                        existing.count++;
                    }
                }
            }
        });

        return groups.sort((a: GroupedAuthor, b: GroupedAuthor) => b.count - a.count);
    }

    get categories() {
        const cats: Category[] = [];
        this.entries.forEach((entry: ToplistEntry) => {
            // oxlint-disable-next-line no-unused-expressions
            entry.podcast &&
                (entry.podcast.categories ?? []).forEach((cat: Category) => {
                    if (!cats.find((c: Category) => c.id === cat.id)) {
                        return !isGeneralCategory(cat) && cats.push(cat);
                    }
                });
        });
        return cats;
    }

    toggleCategoryFilter(filter: string) {
        if (this.categoryFilter.includes(filter)) {
            this.categoryFilter = this.categoryFilter.filter((f: string) => f !== filter);
        } else {
            this.categoryFilter.push(filter);
        }
    }

    clearCategoryFilters() {
        this.categoryFilter = [];
    }

    setCategoryFilters(filters: string[]) {
        this.categoryFilter = filters;
    }

    clearAuthorFilters() {
        this.authorFilter = [];
    }

    setAuthorFilters(filters: string[]) {
        this.authorFilter = filters;
    }

    isInCategoryFilter(name: string) {
        return this.categoryFilter.includes(name);
    }

    isInAuthorFilter(name: string) {
        return this.authorFilter.includes(name);
    }

    toggleAuthorFilter(filter: string) {
        if (this.authorFilter.includes(filter)) {
            this.authorFilter = this.authorFilter.filter((f: string) => f !== filter);
        } else {
            this.authorFilter.push(filter);
        }
    }

    hasFilter() {
        return this.categoryFilter.length > 0 || this.authorFilter.length > 0;
    }

    clearFilter() {
        this.categoryFilter = [];
        this.authorFilter = [];
    }

    search(value: string) {
        this.freeFilter = value;
    }

    private matchesFreeFilter(entry: ToplistEntry) {
        if (this.freeFilter.length === 0) {
            return true;
        } else {
            return entry.podcast && entry.podcast.author
                ? entry.podcast.name.toLowerCase().includes(this.freeFilter.toLowerCase()) ||
                      entry.podcast.author.name.toLowerCase().includes(this.freeFilter.toLowerCase())
                : true;
        }
    }

    setOnlyNew = (value: boolean) => {
        this.onlyNew = value;
    };
}
